<template>
	<div class="checkout-page">
		<empty-cart v-if="noItemsAtMount" />
		<checkout-minimal v-if="showMinimalCheckout" />
		<offer-coupons v-if="!showMinimalCheckout" />
		<offers v-if="!showMinimalCheckout" />
		<div class="columns center--mobile" v-if="!showMinimalCheckout">
			<div :class="styleMainSection()">
				<!-- Contact Details -->
				<checkout-section identity="section-contact" label="Contact Details">
					<contact-details />
				</checkout-section>
				<!-- Service Type -->
				<checkout-section identity="method-section" label="Service Type" :key="serviceSectionKey">
					<ServiceTypeChooser :isStreamLined="true" />
					<p v-if="hasErrors('serviceType')" class="error">{{errors.serviceType}}</p>
					<!-- Leave at door -->
					<div id="leave-at-door">
						<div class="field-body">
							<div class="field">
								<div class="control">
									<div class="flex-column control-group">
										<div class="flex-full-width">
											<label class="control control--checkbox cust-radio" ref="leaveAtDoorCheckbox">
												<input type="checkbox" v-model="leaveAtDoor" role="checkbox" aria-checked="true" aria-label="leave at door">
												<div class="control__indicator checkbox"></div>
												<span v-if="isServiceTypeDelivery">Leave items at my doorstep</span>
												<span v-if="isServiceTypeTakeout">Curbside pickup</span>
											</label>
											<p class="service-info-text" v-if="isServiceTypeTakeout">Please pay in advance below. Please park near the store, call when you arrive, and turn on your hazard lights so we can locate you.</p>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</checkout-section>
				<!-- Address Chooser -->
				<checkout-section identity="address-chooser-section" :label="serviceTypeLabel" v-if="hasSelectedService">
					<div>
						<order-destination v-if="showOrderDestinationSection"/>
						<template v-if="showMap">
							<BaseMap
								:zoom=12
								:directions="(serviceType === EServiceType.SERVICE_TYPE_DELIVERY) ? {origin: location, destination: focusedLocation} : null"
								:storeLocation="location"
								@click="setLocation(deliveryAddress)"
							/>
						</template>
					</div>
					<address-chooser v-if="serviceType === EServiceType.SERVICE_TYPE_DELIVERY && getCustomerId" @error="(value) => errors['address'] = value"/>
					<p v-if="hasErrors('address') && getCustomerId" class="error">{{errors.address}}</p>
				</checkout-section>
				<!-- Delivery/Pickup time chooser -->
				<checkout-section identity="time-chooser-section" label="Time">
					<BaseDatePicker />
					<p v-if="hasErrors('orderTime')" class="error">{{errors.orderTime}}</p>
				</checkout-section>
				<!-- Payment chooser -->
				<checkout-section identity="payment-chooser-section" label="Payment">
					<payment-method-control />
					<template v-if="preorder === EPreorder.PAY_NOW">
						<payment-chooser/>
					</template>
					<p v-if="hasErrors('payment')" class="error">{{errors.payment}}</p>
				</checkout-section>
				<!-- Coupon Field -->
				<checkout-section identity="section-coupon" label="Coupon Code">
					<coupon-field />
				</checkout-section>
				<!-- Special Instructions -->
				<checkout-section identity="section-instructions" label="Notes">
					<checkout-special-instructions />
				</checkout-section>
				<!-- Checkout Summary -->
				<checkout-section identity="section-checkout-summary" label="Order">
					<checkout-summary @change="proposeOrder()"/>
					<div id="c2a" :class="styleMobileCheckoutFooter" class="has-text-centered" @click="checkValidForm()">
						<ul id="errs" class="error">
							<li :key=key v-for="(err, key) in errors">
								{{err}}
							</li>
						</ul>
						<div v-if="$mq === 'mobile'">
							<p class="has-text-centered c2a-order-icons">👍 {{ $appConstants.checkout.icons.foodType }} 👏</p>
						</div>
						<BaseButton
							@click="sendOrder"
							title="Place Order"
							:classNames="['button--modifier', 'addToCart']"
							:disabled="isCheckoutDisabled"
						/>
						<div class="mobile-sidebar">
							<checkout-sidebar
								v-if="$mq === 'mobile'"
								@placeOrder="sendOrder"
							/>
						</div>
					</div>
				</checkout-section>
			</div>
			<div class="column is-4-desktop is-5-tablet is-12-mobile" v-if="($mq === 'desktop')">
				<div :class="{'bottom-tracker-position': $mq === 'desktop' && activeOrder}" class="is-pulled-right-tablet has-text-centered-mobile sidebar">
				<checkout-sidebar
					v-if="$mq !== 'mobile'"
					@placeOrder="sendOrder"
				/>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
// TODO this vue needs to be split up
import * as moment from "moment-timezone";
import {
	ACCOUNT_CHECKOUT,
	LOCATION,
	CART,
	ACCOUNT,
	RECENT_ORDERS,
  	MENU_ITEMS,
} from "@/stores/defs";
import Analytics from '@Analytics';
import { API_CMDS } from '@/common/messages/api.cmds';
import { enforceFifteenMinIncrements } from "@/components/common/BaseDatePicker";
import { ERROR_MSG } from "@/common/messages/error.msg";
import { EServiceType } from "@/models/interfaces/Orders/EServiceType";
import { localStorageAdapter } from "@/services/app.service";
import { mapActions, mapGetters } from "vuex";
import { SUCCESS_MSG } from "@/common/messages/success.msg";
import { getServiceTypeName } from '@/utils/helper.functions';
import CheckoutMinimal from '@/components/checkout/CheckoutView/CheckoutMinimal';
import ContactDetails from '@/components/account/ContactDetails/ContactDetails.vue';
import AddressChooser from "@/components/checkout/AddressChooser";
import AddressModel from '@/models/AddressModel';
import CheckoutSection from "@/components/checkout/CheckoutSection";
import CheckoutSidebar from "@/components/checkout/CheckoutSidebar";
import CheckoutSpecialInstructions from "@/components/checkout/CheckoutSpecialInstructions.vue";
import CheckoutSummary from "@/components/checkout/CheckoutSummary";
import CouponField from '@/components/checkout/CouponField.vue';
import customerService from '@/services/customer.service';
import DateManipulator from '@/utils/DateManipulator';
import EmptyCart from "@/components/checkout/EmptyCart";
import EventBus from '@EventBus';
import Formatter from '@/utils/Formatter';
import GoogleMarkerInfoWindow from "@/components/store-locator/GoogleMarkerInfoWindow";
import Offers from '@/components/checkout/Offers.vue';
import OfferCoupons from '@/components/checkout/OfferCoupons.vue';
import OrderDestination from '@/components/checkout/OrderDestination';
import orderService, {EPreorder} from "@/services/order.service";
import guestService from '@/services/guest.service';
import upsellService from '@/services/upsell.service';
import PaymentChooser from "@/components/checkout/PaymentChooser";
import PaymentMethodControl from '@/components/checkout/PaymentMethodControl.vue';
import ServiceTypeChooser from "@/components/checkout/ServiceTypeChooser";

import headerMixin from "@Mixin/headerMixin";
import checkoutMixin from "@Mixin/checkoutMixin";
import modalMixin from '@Mixin/modalMixin';
import tipsMixin from '@Mixin/tipsMixin';
import upsellMixin from "@Mixin/upsellMixin";
import paymentMixin from "@Mixin/paymentMixin";
import offersMixin from '@Mixin/offersMixin';
import cartMixin from '@Mixin/cartMixin';
import eCommerceAnalyticsMixin from '@/components/mixins/eCommerceAnalyticsMixin';

import { ECheckoutStep } from '@/common/analytic-events/ECheckoutStep';
import { isNaN, forEach, throttle, map, reduce } from 'lodash';

export default {
    mixins: [
		headerMixin,
		checkoutMixin,
		modalMixin,
		tipsMixin,
		upsellMixin,
		paymentMixin,
		offersMixin,
		cartMixin,
		eCommerceAnalyticsMixin,
	],
	components: {
		"empty-cart": EmptyCart,
		'checkout-minimal': CheckoutMinimal,
		"checkout-section": CheckoutSection,
		"contact-details": ContactDetails,
        "address-chooser": AddressChooser,
        "payment-chooser": PaymentChooser,
        "checkout-sidebar": CheckoutSidebar,
		"checkout-special-instructions": CheckoutSpecialInstructions,
		'coupon-field': CouponField,
        "checkout-summary": CheckoutSummary,
        "google-marker-info-window": GoogleMarkerInfoWindow,
        'payment-method-control': PaymentMethodControl,
		'order-destination': OrderDestination,
		'offers': Offers,
		'offer-coupons': OfferCoupons,
        ServiceTypeChooser
    },
    computed: {
        ...mapGetters({
            items: CART.ITEMS,
            userInstructions: ACCOUNT_CHECKOUT.GET_USER_INSTRUCTIONS,
            itemCount: CART.ITEM_COUNT,
			activeOrder: RECENT_ORDERS.GET_ACTIVE_ORDER,
            getTotals: CART.GET_TOTALS,
			guestAddress: ACCOUNT.GET_GUEST_ADDRESS,
			guestId: ACCOUNT_CHECKOUT.GET_GUEST_ID,
			guestPhone: ACCOUNT.GET_GUEST_PHONE,
			menuId: MENU_ITEMS.GET_MENU_ID,
			getLeaveAtDoor: ACCOUNT_CHECKOUT.GET_LEAVE_AT_DOOR,
		}),
        focusedLocation: {
            get: function() {
                if (this.serviceType === this.EServiceType.SERVICE_TYPE_DELIVERY) {
                    return this.deliveryAddress || null;
                } else if (this.serviceType === this.EServiceType.ORDER_FOR_PICKUP) {
                    return this.location;
                }
            }
		},
		isServiceTypeDelivery() {
			return this.serviceType === EServiceType.SERVICE_TYPE_DELIVERY;
		},
		isServiceTypeTakeout() {
			return this.serviceType === EServiceType.SERVICE_TYPE_TAKEOUT;
		},
		serviceTypeLabel() {
			const serviceType = this.serviceType;
			const isPickup = (serviceType === EServiceType.SERVICE_TYPE_TAKEOUT);
			const useNoun = (isPickup) ? 'Location' : 'Address';
			const serviceTypeLabel = this.getServiceTypeName(serviceType);
			return `${serviceTypeLabel} ${useNoun}`;
		},
		hasSelectedService() {
			const serviceType = this.serviceType;
			if (!serviceType) {
				return false;
			}
			return true;
		},
		showOrderDestinationSection() {
			const serviceType = this.serviceType;
			const typePickup = EServiceType.SERVICE_TYPE_TAKEOUT;
			const onDesktop = (this.$mq === 'desktop');
			return (serviceType === typePickup) || onDesktop;
		},
		showMap() {
			const location = this.location;
			const hasApiKey = location.delivery.googleMapsApiKey;
			if (!hasApiKey) {
				return false;
			}
			const serviceType = this.serviceType;
			const isDelivery = (serviceType === EServiceType.SERVICE_TYPE_DELIVERY);
			const isTakeout = (serviceType === EServiceType.SERVICE_TYPE_TAKEOUT);
			const focusedLocation = this.focusedLocation;
			const notMobile = (this.$mq !== 'mobile');
			return ( (isDelivery && focusedLocation) || (isTakeout && location) ) && notMobile;
		},
		getCustomerPhone() {
			if (this.customer) {
				return this.customer.phone;
			}
			return this.guestPhone;
		},
		getInitialAddress() {
			return this.guestAddress || this.deliveryAddress;
		},
		getCustomerId() {
			const customerId = this.customer.id || this.guestId;
			return customerId;
		},
		showMinimalCheckout() {
			const isMobile = (this.$mq === 'mobile');
			return (isMobile && this.minimalCheckout);
		},
		styleMobileCheckoutFooter() {
			const hideFooter = (this.$mq === 'mobile') ? this.hideFooter : false;
			return {
				'hide-for-keyboard': hideFooter,
			};
		},
        errors: {
            get() {
				let errMsgs = {};
				if (this.underDeliveryMinimum()) {
					const minimumDelivery = this.minimumDelivery;
					const displayAmount = Formatter.centsToDollars(minimumDelivery);
					errMsgs.deliveryMinimum = ERROR_MSG.ORDER.DELIVERY.UNDER_MINIMUM(displayAmount);
				}
                if (this.serviceType === EServiceType.SERVICE_TYPE_DELIVERY) {
                    if (!this.deliveryAddress) {
                        errMsgs.address = ERROR_MSG.ADDRESS.MISSING_ADDRESS(
                            this.location.delivery.deliveryRadius,
                            this.location.delivery.deliveryRadiusUnit
                        );
                    }
                    if (!this.paymentMethod && this.preorder === EPreorder.PAY_NOW) {
                        errMsgs.payment = ERROR_MSG.ORDER.PAYMENT.MISSING_PAYMENT;
                    }

                 if (this.deliveryFee === false) {
                     errMsgs.delivery === ERROR_MSG.ADDRESS.MISSING_DELIVERY_PRICE;
                 }

                } else if (this.serviceType === EServiceType.SERVICE_TYPE_TAKEOUT) {
                    if (this.preorder === EPreorder.PAY_NOW && !this.paymentMethod) {
                        errMsgs.payment = ERROR_MSG.ORDER.PAYMENT.MISSING_PAYMENT;
                    }

                    if (!this.location) {
                        errMsgs.address = ERROR_MSG.LOCATION.MISSING_LOCATION;
                    }
                } else if (!this.serviceType) {
                    errMsgs.serviceType = ERROR_MSG.ORDER.SERVICE_TYPE.MISSING_SERVICE_TYPE
                }
                if (!this.orderTime) {
                    errMsgs.orderTime = ERROR_MSG.ORDER.ORDER_TIME.MISSING_ORDER_TIME;
                }

                if (!this.itemCount) {
                    errMsgs.itemCount = ERROR_MSG.ORDER.ITEMS.MISSING_ITEMS;
                }
                return errMsgs;
            }
        }
    },
    data() {
        return {
            EPreorder: EPreorder,
            EServiceType: EServiceType,
            config: {
                altInput: true,
                altFormat: "h:iK, F d, Y",
                placeholder: "Pick a Date and Time",
                enableTime: true,
                minDate: Date.now(),
                minuteIncrement: 15,
                onReady: enforceFifteenMinIncrements,
                onValueUpdate: enforceFifteenMinIncrements,
                disableMobile: "true"
			},
			hideFooter: false,
			gettingPreflight: false,
			noItemsAtMount: false,
			minimalCheckout: false,
			leaveAtDoor: this.getLeaveAtDoor,
			serviceSectionKey: 1,
        };
    },
    methods: {
        showModal(title = "", msg = "") {
            this.triggerModal({
                contentTemplate: msg,
                title: title,
                classes: "global-modal",
                showHeader: !!title
            })

        },
        setError(key, msg = "") {
            if (key === "reset") {
                for (let errKey in this.errors) {
                    this.errors[errKey] = "";
                }
            } else {
                this.errors[key] = msg;
            }
        },
        hasItems() {
            return this.items.length > 0;
        },
        ...mapActions({
            setPreorder: ACCOUNT_CHECKOUT.SET_PREORDER,
            setServiceType: ACCOUNT_CHECKOUT.SET_SERVICE_TYPE,
            setOrderTime: ACCOUNT_CHECKOUT.SET_ORDER_TIME,
            clearCart: CART.CLEAR_CART,
            setTotals: CART.SET_TOTALS,
            setAddress: ACCOUNT_CHECKOUT.SET_DELIVERY_ADDRESS,
            editAddress: ACCOUNT.EDIT_ADDRESS,
            addAddressToCustomer: ACCOUNT.ADD_ADDRESS,
            setDeliveryAddress: ACCOUNT_CHECKOUT.SET_DELIVERY_ADDRESS,
			setGuestAddress: ACCOUNT.SET_GUEST_ADDRESS,
			setGuestId: ACCOUNT_CHECKOUT.SET_GUEST_ID,
			setLeaveAtDoor: ACCOUNT_CHECKOUT.SET_LEAVE_AT_DOOR,
        }),
        setLocation() {
			const isDelivery = (this.serviceType === this.EServiceType.SERVICE_TYPE_DELIVERY);
			const location = (isDelivery) ? this.deliveryAddress : this.location;
			this.focusedLocation = {
				location: location,
				zoom: 13
			};
		},
		getOrderDelay() {
            let deltaSeconds = moment().tz(this.location.timeZone).diff(this.orderTime, "seconds");

            if (deltaSeconds !== 0) {
                deltaSeconds = deltaSeconds * -1;
            }
			return {
				pd: deltaSeconds,
				pdt: 3,
			};
		},
		trackOrder(order, items) {
			const transactionId = order.id;
			this.$tpa.trackCheckoutOrderPage();
			this.$tpa.trackCheckoutTransaction(order);
			forEach(items, (item) => {
				this.$tpa.trackCheckoutItem(item, transactionId);
				const coupon = item.hasCouponDiscount();
				if (coupon) {
					this.$tpa.trackOrderCoupon(coupon.codeName);
				}
				const offer = item.hasOfferDiscount();
				if (offer) {
					this.$tpa.trackOrderOffer(offer.reward_id);
				}
			});
			this.$tpa.trackCheckoutSendTransaction();
			this.$tpa.trackOrderSuccess(transactionId);
			this.customTracking({
				order,
				items,
			})
		},
		customTracking({order, items}) {
			if (!window.dataLayer) {
				return;
			}
			const transactionId = order.id;
			let total = reduce(order.totals, (acc, value, key) => {
				if (key === 'gratuity' || key === 'collected') {
					return acc;
				}
				return acc + (+value);
			}, 0);
			total /= 100;
			const trackedItems = map(items, (item) => this.$tpa.getTrackingItem(item, transactionId));
			window.dataLayer.push({
				event: 'purchase',
				transactionId: transactionId,
				transactionTotal: total,
				transactionProducts: trackedItems,
			});
		},
		getBaseApiReq() {
			return {
				customer: this.getCustomerId,
				items: this.items,
				lb: this.location.id,
				m: this.menuId,
				userInstructions: this.userInstructions,
				serviceType: this.serviceType,
			}
		},
        sendOrder() {
			// don't send order if time is in past
			const minuteOld = moment().add({minutes: -1});
            if (this.orderTime.isBefore(minuteOld)) {
				this.setOrderTime(DateManipulator.roundMomentTo15Min(moment()));
			}
			if (this.itemCount) {
                // instantiate api request
                let delay = null;
                const isDelayed = !DateManipulator.isTimeASAP(this.orderTime);

                if (isDelayed) {
                    delay = this.getOrderDelay();
				}
				let apiReq = this.getBaseApiReq();
				apiReq.delay = delay;

				apiReq = this.handleOrderDiscount(apiReq);

                if (this.serviceType === this.EServiceType.SERVICE_TYPE_DELIVERY) {
                    if (this.deliveryAddress && ( (this.paymentMethod && this.preorder === EPreorder.PAY_NOW) || this.preorder === EPreorder.PAY_LATER ) && this.orderTime) {
                        apiReq.deliveryAddress = this.deliveryAddress;
                        apiReq.preorder = this.preorder;

                        if (this.preorder === EPreorder.PAY_NOW) {
                            apiReq.a = this.paymentMethod.id
                        }

                        this.checkout(apiReq);
                    }
                } else if (this.serviceType === this.EServiceType.SERVICE_TYPE_TAKEOUT) {
                    if (this.location && this.orderTime) {
                         apiReq.serviceType = EServiceType.SERVICE_TYPE_TAKEOUT;

                        // should have payment method if preorder = 0
                        if (this.preorder === EPreorder.PAY_NOW && !this.paymentMethod) {
                            this.errors.payment = ERROR_MSG.ORDER.PAYMENT.MISSING_PAYMENT;
                        } else {
                            // if preorder === 1, pay in store, ELSE, pay now
                            if (this.preorder === EPreorder.PAY_LATER) {
                                apiReq.preorder = EPreorder.PAY_LATER;
                            } else {
                                apiReq.preorder = EPreorder.PAY_NOW;
                                apiReq.a = this.paymentMethod.id;
                            }

                            this.checkout(apiReq);
                        }
                    }
                }
            }
		},
		handleOrderDiscount(order) {
			const coupon = this.cartCoupon;
			if (coupon) {
				if (coupon.isForOrder() && coupon.active) {
					order.dis = [coupon.id];
				}
			}
			return order;
		},
		// TODO this is currently in four different files
		// consolidate saveProperties (only use in accountMixin)
		saveProperties() {
			let apiReq = {
				lb: this.location.id,
				cmd: 'guest_update',
				c: {
					base: this.guestId,
					phone: this.guestPhone,
					email: this.guestEmail,
					firstname: this.guestName,
				}
			};
			return customerService.editProfile(apiReq).catch((err) => {
				console.error(`Error saving guest information: ${err}`);
			});
		},
		checkout(order) {
			this.setOrderInFlight(true);
			const validationPromises = [];
			if (this.isGuest) {
				validationPromises.push(this._validateGuestForOrder(order));
			}
			const errorMessage = 'There was an error placing the order. Please try again later.';
			Promise.all(validationPromises).then((results) => {
				if (!results) {
					this.orderErrorResponse(errorMessage);
				}
				if (this.isPayLater) {
					this.placeOrder(order);
					return;
				}
				this.showTipsModal(() => {
					const tip = this.getTotals.gratuity;
					order.g = tip;
					this.placeOrder(order);
				})
			}).catch(() => {
				this.orderErrorResponse(errorMessage);
			})
		},
		placeOrder(order) {
			const callback = this.orderSuccessfulResponse;
			const errorCallback = this.orderErrorResponse;
			orderService.sendOrder(order).then(callback).catch(errorCallback);
			if (!order.proposeOrder) {
				this.clearStateAfterOrder();
			}
		},
		// TODO this should all happen in the service and change application state from there.
		orderSuccessfulResponse(response) {
			const message = response && response.data && response.data.message;
			if (message === SUCCESS_MSG.ORDER.PROCESSED) {
				this.setError('reset');
				this.showModal(null, SUCCESS_MSG.ORDER.PLACED);
				const orderItems = this.items;
				const orderInfo = response.data.body.api_order;
				this.trackOrder(orderInfo, orderItems);
				upsellService.checkUpsellConversions(orderItems, this);
				this.clearCart();
				setTimeout(() => {
                    this.$modal.hide("contentModal");
					this.$router.push({
						path: "/order/order-confirmation",
						query: {
							order_id: response.data.body.order.order_id,
							lb: response.data.body.order.location_base
						},
						params: {
							checkoutOrder: response.data.body
						}
					});
					this.setOrderInFlight(false);
					this.setShowUpsellOff();
					this.$tpa.trackCheckoutClearTransaction();
				}, 1500);
			} else {
                let errorMessage = '';
                if (response.data.message.includes("couldn't contact")) {
                    errorMessage = ERROR_MSG.LOCATION.UNAVAILABLE(this.location.contactPhone)
                } else {
                    errorMessage = response.data.message;
                }
				this.showModal("Request Error", errorMessage);
				this.$tpa.trackOrderFailure(errorMessage);
				setTimeout(() => {
					this.$modal.hide("contentModal");
					this.setOrderInFlight(false);
				}, 3000);
			}
		},
		orderErrorResponse(error) {
			const errorMessage = error.toString();
			this.setOrderInFlight(false);
			this.showModal("Request Error", errorMessage);
			this.$tpa.trackOrderFailure(errorMessage);
			setTimeout(() => {
				this.$modal.hide("contentModal");
				this.setOrderInFlight(false);
			}, 3000);
			console.error(`Error in sending order: ${error}`);
		},
		hasErrors(type) {
			if (this.errors[type]) {
				return true;
			}
			return false;
		},
        recalculateOrder() {
            this.gettingPreflight = false;
            this.proposeOrder();
		},
        proposeOrder() {
            // if (!this.gettingPreflight) {
				let apiReq = {
					...this.getBaseApiReq(),
					a: null,
					deliveryAddress: this.deliveryAddress || null,
					preorder: EPreorder.PAY_NOW,
					proposed: 1,
				}
				if (this.isGuest) {
					 // This is ok to be non-blocking for a proposed order
					const validateCustomer = customerService.validateCustomer(this.guestId);
					validateCustomer.then((result) => {
						if (!result) {
							console.error('error validating customer');
						}
					});
				}
				apiReq = this.handleOrderDiscount(apiReq);
                if(this.paymentMethod && this.paymentMethod.id) {
                    apiReq.a = this.paymentMethod.id;
                }
                this.gettingPreflight = true;
                if (this.itemCount) {
                    orderService
                        .sendOrder(apiReq)
                        .then(res => {
                            if (res.data.message === SUCCESS_MSG.ORDER.PROPOSED) {
                                const { sub_total, ...totals } = res.data.body.api_order.totals;
                                this.setTotals({ ...totals, subTotal: res.data.body.api_order.totals.sub_total });
                                // use the api to determine delivery availability
                                if (this.location.delivery.hasDeliveryRegionsSet) {
                                    if (res.data.body.order.delivery_details) {
                                        this.deliveryAddress.setDeliveryInformation(res.data.body.order.delivery_details);
                                        // update isDeliverable
                                        this.editAddress(this.deliveryAddress);
                                        if (!this.deliveryAddress.delivery.isDeliverable) {
                                            this.setAddress(null);
                                            this.errors.address = ERROR_MSG.ADDRESS.MISSING_ADDRESS(
                                                this.location.delivery.deliveryRadius,
                                                this.location.delivery.deliveryRadiusUnit
                                            );

                                        } else if (this.deliveryAddress) {
                                            this.errors.addresss = "";
                                        }
                                    }
                                }
                            }

                            this.gettingPreflight = false;
                        })
                        .catch(err => {
                            console.log(err);
                            this.gettingPreflight = false;
                        });
                }
            // }
		},
		useAddressForRequest(address) {
			const customerId = this.getCustomerId;
			const customerPhone = this.getCustomerPhone;
			return {
				address: address.formattedAddress,
				b: this.location.id,
				cb: customerId,
				cmd: API_CMDS.ADDRESS.ADD,
				latitude: address.latitude,
				longitude: address.longitude,
				phone: customerPhone,
				...(address.aptNum && { aptNum: address.aptNum }),
				...(address.note && { note: address.note })
			}
		},
		addInitialAddress() {
			const customerId = this.getCustomerId;
			const initialAddress = this.getInitialAddress;
            if (initialAddress) {
                const apiReq = this.useAddressForRequest(initialAddress);
                let addAddress = false;
                if (!this.customer.addresses.length) {
                    addAddress = true;
                } else {
                    // make sure address isn't in list of addresses
                    addAddress = !this.customer.addresses.some((address) => {
                        return address.latitude === initialAddress.latitude &&
                               address.longitude === initialAddress.longitude &&
                               address.aptNum === initialAddress.aptNum &&
                               address.note === initialAddress.note;
                    });
                }
                if (addAddress && customerId) {
                    customerService.addDeliveryAddress(apiReq).then((res) => {
						if (res.data.body.address) {
							const newAddress = new AddressModel(res.data.body.address);
							this.setServiceType(EServiceType.SERVICE_TYPE_DELIVERY);
							this.addAddressToCustomer(newAddress);
							this.setGuestAddress(null);
							this.setDeliveryAddress(newAddress);
						}
					}).catch((err) => {
						console.log(err);
					});
                }
            }
        },
        styleMainSection() {
            if (this.$mq === 'mobile' || this.$mq === 'tablet') {
                return 'is-11';
            }
            return 'column is-8-desktop is-7-tablet is-12-mobile';
		},
		checkForOffers() {
			const applyOffer = this.getApplyOffer;
			if (applyOffer) {
				this.applyOfferItem(applyOffer);
				this.setApplyOffer(null);
				this.recalculateOrder();
			}
		},
		handleStoreClosedFutureOrder() {
			this.$modal.show("dialog", {
				text: ERROR_MSG.ORDER.ORDER_TIME.STORE_LATE_ORDER
			});
		},
		handleHideFooterOnSmallDevice() {
			const el = this.$el;
			el.addEventListener('click', (event) => {
				const target = event.target;
				// tap an input field
				if (target.classList.contains('input')) {
					this.hideFooter = true;
				}
			});
			let lastPosition = 0;
			const setFooterVisible = (delta) => {
				lastPosition = window.scrollY;
				const docPadding = 200;
				const docHeight = document.body.clientHeight;
				const totalHeight = document.body.scrollHeight;
				const scrollToBottom = (lastPosition + docHeight) > (totalHeight - docPadding);
				const scrollToTop = (lastPosition < docPadding);
				if (scrollToBottom) {
					this.hideFooter = false;
					return;
				}
				if (delta < 0 && !scrollToTop) { // scroll down
					this.hideFooter = false;
				} else if (delta > 0 || scrollToTop) { // scroll up
					this.hideFooter = true;
				}
			}
			const scrollListener = (event) => {
				let delta = lastPosition - window.scrollY;
				window.requestAnimationFrame(() => setFooterVisible(delta));
			}
			const throttleListener = throttle((event) => scrollListener(event), 500);
			window.addEventListener('scroll', throttleListener);
		},
		mobileSetup() {
			if (this.$mq !== 'mobile') {
				return;
			}
			this.handleHideFooterOnSmallDevice();
			const appConstants = this.$appConstants;
			const header = appConstants.header;
			const checkoutHeaderItems = header.routes.checkout;
			this._setHeaderItems(checkoutHeaderItems);
			this._setHeaderTitle('Checkout');
			if (!this._hasBackRoute) {
				this._setBackRoute('home');
			}
		},
    },
    mounted() {
		this.proposeOrder();
		// TODO check pizza guys test case for pre-adding address
        // this.addInitialAddress();
		this.checkForOffers();
		if (!this.hasItems()) {
			this.noItemsAtMount = true;
		}
		this.mobileSetup();
		if (!this.minimalCheckout) {
			setTimeout(() => {
				window.requestAnimationFrame(() => {
					const rootElement = this.$root.$el;
					const transitionContainer = rootElement.querySelector('.transition-route-container');
					transitionContainer.style.transform = '';
				});
			}, 1000);
		}
		if (this.serviceType === null) {
			this.setServiceType(EServiceType.SERVICE_TYPE_TAKEOUT);
			this.serviceSectionKey++;
		}
		this.leaveAtDoor = this.getLeaveAtDoor;
		this._initializeCart();
	},
    watch: {
        itemCount(newCount, oldCount) {
            if (newCount !== 0) {
                this.proposeOrder();
            }
        },
        deliveryAddress(newAddress, oldAddress) {
            if (newAddress) {
                this.proposeOrder();
            }
        },
        serviceType(newServiceType) {
			if (newServiceType === EServiceType.SERVICE_TYPE_TAKEOUT) {
				this.leaveAtDoor = false;
				this.setLeaveAtDoor(false);
			}
            this.proposeOrder();
		},
		leaveAtDoor(state) {
			this.setLeaveAtDoor(state);
			if (state) {
				this.setPreorder(EPreorder.PAY_NOW);
			}
		},
		guestId(newId, oldId) {
        	this.proposeOrder();
			// console.log(newId, oldId);
		}
    },
	created() {
		const checkout = this.$appConstants.checkout;
		const vue = this;
		this.minimalCheckout = (checkout.minimal === '1') ? true : false;
		EventBus.$on('recalculate-order', this.recalculateOrder);
		EventBus.$on('future-date-only', this.handleStoreClosedFutureOrder);
		EventBus.$on('customer-update', this.addInitialAddress);
		EventBus.$on('reset-error-message', this.setError);
	},
	destroyed() {
		EventBus.$off('recalculate-order');
		EventBus.$off('future-date-only');
		EventBus.$off('customer-update');
		EventBus.$off('reset-error-message');
	},
	beforeRouteEnter(to, from, next) {
		next((vue) => {
			vue.mobileSetup();
		});
	}
};
</script>

<style lang="scss">
@import "globalStyles";
$font-family: sans-serif, Arial;

.align-right {
    margin-left: auto;
}

.field-label {
	margin-right: 4em;
	min-width: 9em;
	&.order-label-text {
		min-width: 4em;
	}
}

.flex-full-width {
    flex: 0 0 100%;
}

.row-wrap {
    flex-flow: row wrap;
}
.flex-column {
	flex-direction: column;
	font-family: $secondaryFont;
}

.base-date-picker input {
    font-size: 1em;
    border: none;
    font-family: $font-family;
    text-align: right;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    color: #3273dc;
    width: 100%;
}

#leave-at-door {
	.control__indicator.checkbox {
		position: absolute;
		top: 0.3em;
	}
	.flex-full-width {
		padding-top: 1.5em;
	}
	span {
		margin-left: 0.4em;
		font-size: 16px;
	}
}

.dayContainer,
.flatpickr-weekday,
.flatpickr-current-month span.cur-month {
    font-family: $font-family;
    font-size: 1em;
}
.base-date-picker input::-webkit-input-placeholder {
    text-align: right;
    color: $navigationItemBorderColor;
}

#c2a {
    margin-top: 2em;
	transform: translate(0, 0);
	transition: .3s transform ease-out;
}

#c2a.hide-for-keyboard {
	transform: translate(0, 100%);
}

.field-label .label {
    font-weight: bold;
    font-size: 1.25rem;
    text-align: left;
}

.checkout-radio {
    margin-left: -4px !important;
    margin-bottom: 0em;
}

.no-padding-left {
    padding-left: 0em !important;
}
@include desktop {
    .is-paddingless-vertical {
        padding-bottom: 0em !important;
        padding-top: 0em !important;
    }

    .sidebar {
        position: sticky;
        top: 7em;
	}

    #c2a .button-wrapper {
        width: 50%;
        margin: auto;
    }
}
@include mobile {
	.center--mobile {
		display: flex;
		justify-content: center;
		flex: 1;
	}
    .checkout-page {
		padding-top: 1em;
		margin-bottom: 2em;
		min-height: 100vh;
        .mobile-radio {
            margin: 0rem !important;
        }
    }

    #c2a {
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        background-color: white;
        border-top: 1px solid $greyOutline;
		z-index: $zTopInteractionLayer;
        button {
            min-width: 68%;
            max-width: 68%;
		}
		li {
			margin: 1em;
		}
    }

    .mobile-sidebar {
        padding: 1em 0em;
    }

    .c2a-order-icons {
        padding: 0.2em 0em;
        font-size: 2em;
	}
}
#errs {
    margin-bottom: 1em;
}
@include phone {
	.center--mobile {
		display: block;
	}
	#method-section {
		.radio-button-container {
			margin: 5px 0;
			.option-choice-name {
				max-width: 80vw;
			}
		}
	}
}
</style>



// WEBPACK FOOTER //
// src/components/checkout/Checkout.vue